/**
 * Element + blowing collection model for the high-lift airfoil (FXC-9009 §B.5 — the
 * "load-bearing" restructure that unblocks add/move foils, control-surface synthesis and
 * multi-blowing).
 *
 * The historical {@link HighLiftAirfoil} shape models the section as a *fixed triple* of named
 * fields (`design.cove` for the main, `design.vane`, `design.flaperon`) plus exactly one
 * top-level `propeller`. That makes "add a second vane" or "two blowing disks" impossible to
 * express. This module introduces the canonical COLLECTION model:
 *
 *   - `design.elements: Element[]` — a tagged union (`main-cove` | `vane-bspline` |
 *     `flaperon-derived`, with `plain-flap` reserved for the control-surface tranche). The
 *     default section is exactly `[main-cove, vane-bspline, flaperon-derived]`.
 *   - `design.blowing: BlowingSource[]` — the actuator-disk blowing sources. The default has the
 *     single propeller as `blowing[0]`.
 *
 * **This tranche keeps the legacy nested fields as the values the geometry builders + UI read.**
 * The collection is the canonical *index* over those fields: each element/blowing source carries
 * a `ref` back to the legacy field it projects (see {@link deriveElements}). `buildConfiguration`
 * and `buildSweepInputs` now iterate the collection, and a one-time {@link migrateConfig} maps any
 * legacy-shaped saved/Tailor/cached config into the new shape. The next tranche flips the legacy
 * fields to *derive* from the collection and adds the add/move/control-surface UI + the
 * multi-element/multi-disk wire; this tranche must stay byte-identical for the default config so
 * existing cross-project cache HITs (handover gotcha #4) survive.
 */

import type {
  AirfoilCoords,
  CoveCutouts,
  FlaperonShape,
  HighLiftAirfoil,
  HighLiftDesign,
  Propeller,
  V2,
  VaneShape,
} from './estolConfig';
import { fitSlottedSystemToSection } from './fitElements';

/**
 * Schema version stamped on every {@link HighLiftAirfoil}. Bumped whenever the persisted shape
 * changes so {@link migrateConfig} can detect-and-upgrade saved / Tailor / cached configs.
 *
 *   - (absent) — the original legacy shape (`{main, design:{cove,vane,flaperon,…}, propeller}`).
 *   - 2        — element + blowing collections introduced (this tranche, FXC-9009 §B.5).
 */
export const HIGH_LIFT_SCHEMA_VERSION = 2 as const;

/**
 * The element-kind tags.
 *   - `plain-flap`: a simple hinged trailing-edge flap (aileron) cut from the main with a cove +
 *     gap, deflected about a hinge (FXC-9009 control-surface tranche, AC-6).
 *   - `plain-slat`: DEPRECATED — the crude original LE slat (the main's nose chunk rotated about a
 *     hinge, with no shaped slot). Kept only to render legacy-persisted / Tailor configs that still
 *     carry it; the `+ slat` palette button + {@link addSlat} now emit the proper `slat` kind. New
 *     code should use `slat`.
 *   - `slat`: the PROPER leading-edge slat (FXC-9009 LE-device tranche) — a shaped slat with a
 *     concave cusped underside that, with the main's leading-edge cove, forms a converging LE slot.
 *     Carries `variant: 'slotted' | 'sealed' | 'fixed-slot'`.
 *   - `krueger`: a Krueger flap — a panel hinged near the LOWER leading edge that rotates
 *     DOWN+FORWARD from under the nose. The main keeps its upper LE. Sealed or bullnose variants.
 *
 * NOTE: a DROOP NOSE / variable-camber LE is NOT an element kind — it is a continuous morph of the
 * main contour (optional `droopHingeX` / `droopDeg` fields on {@link MainCoveElement}).
 */
export type ElementKind =
  | 'main-cove'
  | 'vane-bspline'
  | 'flaperon-derived'
  | 'plain-flap'
  | 'plain-slat'
  | 'slat'
  | 'krueger';

/** Shared fields on every element in the collection. */
interface ElementBase {
  /** Stable, unique id within the collection (drives handle descriptors + provenance keys). */
  id: string;
  /** Human label for the element list / legend. */
  label: string;
}

/** The coved main element (LS(1)-0417 + cove cutout). Always present; always element 0. */
export interface MainCoveElement extends ElementBase {
  kind: 'main-cove';
  /** Cove cutout parameters (projects {@link HighLiftDesign.cove}). */
  cove: CoveCutouts;
  /**
   * DROOP NOSE / variable-camber LE (FXC-9009 LE-device tranche). When `droopDeg` is non-zero the
   * main's nose forward of `droopHingeX` (~0.10–0.15c) is morphed DOWNWARD continuously, blended
   * C1 at the hinge — NOT a split, a camber deflection of the nose. Absent / 0 ⇒ no droop ⇒ the
   * default config stays byte-identical (the morph is a no-op for `droopDeg === 0`).
   */
  droopHingeX?: number;
  /** Droop angle (deg). POSITIVE = nose DOWN. Absent / 0 ⇒ no droop. */
  droopDeg?: number;
}

/** A free-form closed periodic cubic B-spline vane (projects {@link HighLiftDesign.vane}). */
export interface VaneBSplineElement extends ElementBase {
  kind: 'vane-bspline';
  shape: VaneShape;
}

/** The main-derived flaperon (5-pt clamped B-spline nose; projects {@link HighLiftDesign.flaperon}). */
export interface FlaperonDerivedElement extends ElementBase {
  kind: 'flaperon-derived';
  shape: FlaperonShape;
  /**
   * FRISE aileron variant (FXC-9009 control-surface tranche). When `true` the aileron grows a
   * protruding LOWER-surface nose HORN/beak AHEAD of the hinge: a small rounded arc extending the
   * lower nose forward of the cut so that, on trailing-edge-UP (aileron-up / negative deflection)
   * deflection, the horn swings down BELOW the wing's lower surface for aerodynamic + drag balance
   * (the Frise mechanism for adverse-yaw control). Absent / `false` ⇒ a plain flaperon nose ⇒ the
   * default config is byte-identical (the horn is a no-op when off).
   */
  frise?: boolean;
  /** Frise horn arc size as a fraction of chord (the beak's forward reach + radius). Default ~0.03c. */
  friseHornFraction?: number;
}

/**
 * A plain (xfoil-style) hinged trailing-edge flap — a simple aileron cut from the main with a cove
 * and a gap (FXC-9009 control-surface tranche, AC-6). The main is re-cut with a cove at the hinge
 * ({@link cove}), the aft portion split off as this element (the same blunt-TE scan + aft surfaces +
 * cove-mating B-spline nose as {@link FlaperonShape}), and rotated about the hinge by
 * {@link deflectionDeg} (TE-DOWN positive; NEGATIVE = aileron-UP).
 *
 * The deflection lives ON the element (per-element, so a left/right aileron pair can deflect
 * asymmetrically — DECISION-a). The legacy main/vane/flaperon Operation-driven deflection is left
 * unchanged so the default config's design_hash / golden invariants don't move.
 */
export interface PlainFlapElement extends ElementBase {
  kind: 'plain-flap';
  /** Hinge chordwise location (chords) — matches the flexfoil prior's `FlapSpec.hinge_x`. */
  hingeX: number;
  /** Optional hinge vertical location (chords, global). Absent ⇒ the camber midpoint at `hingeX`. */
  hingeY?: number;
  /** Flap deflection about the hinge (deg). POSITIVE = trailing edge DOWN; NEGATIVE = aileron-UP. */
  deflectionDeg: number;
  /**
   * Cove cutout left in the main at the hinge. `coveX` is the cove back wall (the hinge);
   * `upperCutX`/`lowerCutX` are the lip cuts. Sealed flap ⇒ all three equal `hingeX`; a slotted
   * flap ⇒ a lip aft of the hinge (`upperCutX`/`lowerCutX` > `coveX`). Maps `coveDepth` onto the
   * lip offsets (DECISION-b).
   */
  cove: CoveCutouts;
  /**
   * The aft (flap) contour's B-spline nose + cut definition, mirroring {@link FlaperonShape}: aft of
   * the cuts the upper/lower surfaces are the airfoil (blunt-truncated at the TE); forward of them
   * the nose is a slope-matched clamped cubic B-spline that mates the cove.
   */
  shape: FlaperonShape;
  /**
   * FRISE aileron variant (FXC-9009 control-surface tranche) — see {@link FlaperonDerivedElement.frise}.
   * When `true` the flap grows a protruding LOWER-surface nose HORN ahead of the hinge that swings
   * below the wing on TE-up (negative) deflection. Absent / `false` ⇒ plain nose ⇒ byte-identical.
   */
  frise?: boolean;
  /** Frise horn arc size as a fraction of chord (the beak's forward reach + radius). Default ~0.03c. */
  friseHornFraction?: number;
  /**
   * HINGE-CONCENTRIC nose (FXC-9009 internally-hinged sealed aileron — the aircraft-CS seed). When
   * `true` the flap's forward profile is a CIRCULAR ARC centred on the rotation hinge (radius = the
   * max hinge→cut-point distance, minus the slot `gap`), and the main `cove.arc` carries the matching
   * concentric back wall (radius R + gap about the SAME hinge). A circle rotated about its own centre
   * maps to itself, so the slot clearance stays exactly `gap` at EVERY deflection — the ONLY nose
   * geometry that keeps clearance constant through the whole rotation. Absent / `false` ⇒ the legacy
   * slope-matched B-spline nose + vertical cove wall ⇒ byte-identical (the default plain flap, all
   * existing tests, and non-concentric callers are untouched). Set ONLY by the aircraft-CS seed.
   */
  concentricNose?: boolean;
}

/**
 * A leading-edge slat — the fore/aft MIRROR of a {@link PlainFlapElement}. The nose of the main is
 * split off forward of `cutX`, the main carries a slat-cove on its leading edge, and the slat is
 * rotated down/forward by {@link deflectionDeg} (slat droop). The slat element is its own foil;
 * `cutX` is where the main's surfaces are cut (forward of it = the slat).
 */
export interface PlainSlatElement extends ElementBase {
  kind: 'plain-slat';
  /** Chordwise cut where the slat splits from the main nose (chords). Forward of it = the slat. */
  cutX: number;
  /** Hinge chordwise location the slat rotates about (chords). Typically near/aft of `cutX`. */
  hingeX: number;
  /** Optional hinge vertical location (chords, global). Absent ⇒ the camber midpoint at `hingeX`. */
  hingeY?: number;
  /** Slat droop about the hinge (deg). POSITIVE = leading edge DOWN/forward (the deployed slat). */
  deflectionDeg: number;
}

/**
 * A PROPER leading-edge slat (FXC-9009 LE-device tranche) — replaces the crude {@link PlainSlatElement}.
 * The part of the main forward of `cutX` is reshaped into a real slat profile: its UPPER + LE skin is
 * the airfoil's true suction surface (faithful), but its aft/underside is a CONCAVE CUSPED SLOT
 * surface (a B-spline pulled up into the body) that feathers to a thin trailing edge. The MAIN gets a
 * rounded leading-edge cove ({@link buildLeadingEdgeCove}); the slat's cusped underside + that cove
 * form a converging SLOT, riggable by `deflectionDeg` + `gap` + `overlap` (like the flap slot).
 *
 * The `variant` flag selects the three slat sorts that share one builder (FXC-9009 sealed/fixed-slot):
 *   - `slotted`   — the deployed slot (default).
 *   - `sealed`    — deployed with ZERO gap: the slat feathered TE seals onto the main LE (no slot →
 *                   the main uses the blunt trim, not the rounded cove).
 *   - `fixed-slot`— a permanent, non-retracting gap: geometrically identical to slotted; the flag
 *                   only changes the UI affordance (deflection is part of the design, no stow).
 */
export interface SlatElement extends ElementBase {
  kind: 'slat';
  /** Chordwise cut where the slat splits from the main nose (chords). Forward of it = the slat. */
  cutX: number;
  /** Hinge / track chordwise location the slat rotates about (chords). Typically near `cutX`. */
  hingeX: number;
  /** Optional hinge vertical location (chords, global). Absent ⇒ the camber midpoint at `hingeX`. */
  hingeY?: number;
  /** Slat deflection about the hinge (deg). POSITIVE = leading edge DOWN/forward (the deployed slat). */
  deflectionDeg: number;
  /** Slot throat normal distance (chords) — the rigged gap between slat underside and main cove. */
  gap: number;
  /** Chordwise overlap (chords) — positive = the slat TE overlaps the main LE (rigged aft offset). */
  overlap: number;
  /** How concave the slat's cusped underside is (chords) — the slot-ceiling depth on the slat side. */
  slotDepth: number;
  /** The slat's feathered trailing-edge thickness (chords) — a small blunt rather than a full face. */
  teThickness: number;
  /** Slat variant: 'slotted' (default) | 'sealed' (zero gap) | 'fixed-slot' (permanent gap). */
  variant: 'slotted' | 'sealed' | 'fixed-slot';
}

/**
 * A KRUEGER FLAP (FXC-9009 LE-device tranche) — a panel hinged near the LOWER leading edge that
 * rotates DOWN+FORWARD from under the nose. UNLIKE a slat, the main keeps its UPPER LE; the Krueger
 * is a LOWER-surface fold-out: the panel is the main's lower-LE skin from `lowerHingeX` forward
 * around the LE to `noseCutX`, deployed by rotating about `[lowerHingeX, hingeY]`. The main loses its
 * lower-LE skin forward of the hinge (a small fixed lower-LE cove the panel folds out of).
 */
export interface KruegerElement extends ElementBase {
  kind: 'krueger';
  /** Lower-surface hinge chordwise location (chords, ~0.02–0.05c). The panel hinges here. */
  lowerHingeX: number;
  /** Optional hinge vertical location (chords, global). Absent ⇒ the lower-surface y at `lowerHingeX`. */
  hingeY?: number;
  /** Forward cut on the panel (chords) — the panel runs from `lowerHingeX` around the LE to here. */
  noseCutX: number;
  /** Krueger deflection (deg). POSITIVE = down/forward (the deployed panel). */
  deflectionDeg: number;
  /** Bullnose variant: round the deployed leading edge (vs a knife/sealed edge). */
  bullnose: boolean;
  // --- FXC-9009 Krueger rebuild (rebuilt buildKrueger) — all OPTIONAL so old persisted elements
  //     load cleanly; `makeKruegerElement` supplies defaults and `buildKrueger` reads via `?? default`.
  /** Deployed-panel skin ARC LENGTH walked along the lower surface around the LE (chords). Decoupled
   *  from `noseCutX`; sized so a 65° deploy projects a visible forward extension ahead of the LE. */
  panelArcFraction?: number;
  /** Front-loaded convex bow of the suction face, as a FRACTION of panel arc length (0 ⇒ flat/rigid). */
  panelCamber?: number;
  /** Rounded D-nose radius at the forward/lower tip (chords); the system's effective LE radius.
   *  Canonical nose-shape source; promotes the legacy `bullnose` bool (true→default, false→0). */
  bullnoseRadius?: number;
  /** Panel leaf thickness (chords) — OWN parameter, NOT coupled to the main `bluntThickness` (D6). */
  panelThickness?: number;
  /** Slot throat vs the fixed main lower nose (chords). Inert when `variant='sealed'`. */
  gap?: number;
  /** Chordwise standoff of the panel TE region vs the fixed main LE (chords). Inert when sealed. */
  overlap?: number;
  /** Two-state (drops the slat's 'fixed-slot', D8). sealed = primary v1 deliverable; slotted opens a
   *  converging throat from the recess lip + panel edge. Encoded slotted=0, sealed=1 in the fingerprint. */
  variant?: 'sealed' | 'slotted';
}

/** The element tagged union. Order in `design.elements` is significant (provenance indexing). */
export type Element =
  | MainCoveElement
  | VaneBSplineElement
  | FlaperonDerivedElement
  | PlainFlapElement
  | PlainSlatElement
  | SlatElement
  | KruegerElement;

/**
 * An actuator-disk blowing source (the only blowing primitive). The default config's single
 * propeller becomes `blowing[0]`. `loading` (the operating-point knob Δp/q∞) lives on
 * {@link Operation.diskLoading} today for the single default disk; per-source loading is a
 * later-tranche concern, so this carries only the disk GEOMETRY for now.
 */
export interface BlowingSource {
  /** Stable, unique id within the collection. */
  id: string;
  /** Human label for the blowing-source list. */
  label: string;
  /** Disk centre x (chords, global). Negative = ahead of the wing leading edge. */
  x: number;
  /** Disk centre z (vertical, chords). */
  z: number;
  /** Disk diameter = slipstream-band height (chords). */
  diameter: number;
  /** Thrust-axis tilt from the chordwise (deg); positive deflects the jet upward. */
  tiltDeg: number;
}

// ---------------------------------------------------------------------------
// Default collection ids (stable — used by provenance/handle descriptors)
// ---------------------------------------------------------------------------

export const DEFAULT_MAIN_ELEMENT_ID = 'main';
export const DEFAULT_VANE_ELEMENT_ID = 'vane';
export const DEFAULT_FLAPERON_ELEMENT_ID = 'flaperon';
export const DEFAULT_BLOWING_SOURCE_ID = 'prop';

// ---------------------------------------------------------------------------
// Derivation: legacy nested fields → canonical collection (this tranche's seam)
// ---------------------------------------------------------------------------

/**
 * Project the legacy nested design fields into the canonical element collection. The element
 * `shape`/`cove` objects are SHARED references onto `design.cove` / `design.vane` /
 * `design.flaperon` so the collection is a live index, not a copy — mutating either side stays
 * consistent within a single config object. (Callers that need independent objects clone first;
 * `useFoilDesign.mutate` always works on a fresh `structuredClone`.)
 *
 * The default 3-element order is `[main-cove, vane-bspline, flaperon-derived]` — the exact order
 * the geometry builders and the sweep wire expect, so iterating the derived collection reproduces
 * the legacy fixed-triple behaviour byte-for-byte.
 */
export function deriveElements(design: HighLiftDesign): Element[] {
  return [
    { kind: 'main-cove', id: DEFAULT_MAIN_ELEMENT_ID, label: 'Main', cove: design.cove },
    { kind: 'vane-bspline', id: DEFAULT_VANE_ELEMENT_ID, label: 'Vane', shape: design.vane },
    {
      kind: 'flaperon-derived',
      id: DEFAULT_FLAPERON_ELEMENT_ID,
      label: 'Flaperon',
      shape: design.flaperon,
    },
  ];
}

/** Project the legacy single `propeller` into a one-source blowing collection. */
export function deriveBlowing(propeller: Propeller): BlowingSource[] {
  return [
    {
      id: DEFAULT_BLOWING_SOURCE_ID,
      label: 'Propeller',
      x: propeller.x,
      z: propeller.z,
      diameter: propeller.diameter,
      tiltDeg: propeller.tiltDeg,
    },
  ];
}

/**
 * Resolve the canonical element collection for a config: the explicit `design.elements` if present
 * (the new shape), otherwise derived from the legacy nested fields. This is the single accessor
 * the geometry builders + sweep payload call so they iterate the collection regardless of which
 * shape the config was loaded in.
 */
export function elementsOf(cfg: HighLiftAirfoil): Element[] {
  const explicit = cfg.design.elements;
  if (explicit && explicit.length > 0) return explicit;
  return deriveElements(cfg.design);
}

/**
 * Resolve the canonical blowing collection for a config (explicit, else derived from `propeller`).
 * Checks PRESENCE (`!== undefined`), not length: an explicit `[]` is a real, intentional "no blowing
 * sources" state (e.g. the user removed the default propeller — FXC-9009), and must not be treated
 * the same as "never set" or the removal would silently re-derive the same propeller from the
 * untouched legacy `cfg.propeller` field on every read, making the remove button a no-op.
 */
export function blowingOf(cfg: HighLiftAirfoil): BlowingSource[] {
  const explicit = cfg.design.blowing;
  if (explicit !== undefined) return explicit;
  return deriveBlowing(cfg.propeller);
}

// ---------------------------------------------------------------------------
// Typed accessors — resolve a single legacy element from the collection
// ---------------------------------------------------------------------------

/** The first `main-cove` element's cove (the main element is always present). */
export function mainCoveOf(cfg: HighLiftAirfoil): CoveCutouts {
  for (const el of elementsOf(cfg)) if (el.kind === 'main-cove') return el.cove;
  return cfg.design.cove;
}

/** The first `vane-bspline` element's shape, or `null` if the section has no vane. */
export function firstVaneOf(cfg: HighLiftAirfoil): VaneShape | null {
  for (const el of elementsOf(cfg)) if (el.kind === 'vane-bspline') return el.shape;
  return null;
}

/** The first `flaperon-derived` element's shape, or `null` if the section has no flaperon. */
export function firstFlaperonOf(cfg: HighLiftAirfoil): FlaperonShape | null {
  for (const el of elementsOf(cfg)) if (el.kind === 'flaperon-derived') return el.shape;
  return null;
}

/** Keep the first legacy-role collection entries pointed at the legacy mutation surface. */
function rebindLegacyElements(design: HighLiftDesign): void {
  if (!design.elements || design.elements.length === 0) {
    design.elements = deriveElements(design);
    return;
  }
  let main = false, vane = false, flaperon = false;
  for (const el of design.elements) {
    switch (el.kind) {
      case 'main-cove':
        if (!main) el.cove = design.cove;
        main = true;
        break;
      case 'vane-bspline':
        if (!vane) el.shape = design.vane;
        vane = true;
        break;
      case 'flaperon-derived':
        if (!flaperon) el.shape = design.flaperon;
        flaperon = true;
        break;
      case 'plain-flap':
      case 'plain-slat':
      case 'slat':
      case 'krueger':
        // Control-surface / LE-device elements carry their OWN params (no legacy field to rebind onto).
        break;
    }
  }
}

// ---------------------------------------------------------------------------
// Migration — legacy shape → current schema (detect-and-upgrade)
// ---------------------------------------------------------------------------

/**
 * One-time migration of any saved / Tailor / cached config into the current schema. Idempotent:
 * a config already at {@link HIGH_LIFT_SCHEMA_VERSION} is deep-cloned and normalized. A legacy
 * config (`schemaVersion` absent / < current) is upgraded by deriving `design.elements` and
 * `design.blowing` from the legacy nested fields and stamping the version. The legacy fields are
 * PRESERVED (this tranche keeps reading them); they are the values the collection references.
 *
 * The migration never throws on a well-formed legacy `HighLiftAirfoil`; partially-shaped objects
 * (e.g. a Tailor record that only carried flattened axes) are out of scope — callers reconstruct a
 * full config from axes via `applyAxesToConfig` before persisting.
 */
export function migrateConfig(legacy: HighLiftAirfoil): HighLiftAirfoil {
  const cfg = structuredClone(legacy);
  // Derive the collection from whatever legacy fields are present, but keep any elements/blowing
  // the caller already supplied (so a forward-compatible config isn't clobbered).
  rebindLegacyElements(cfg.design);
  // Presence, not length: an explicit `[]` means the collection has genuinely been emptied (e.g. the
  // user removed the default propeller — FXC-9009) and must survive this normalize pass unchanged;
  // only an ABSENT collection (a true pre-restructure legacy config) gets derived from `propeller`.
  // `setConfig`/`loadConfig` run every load through this function, so getting this wrong would make
  // a removal un-persist itself the next time the section is loaded.
  if (cfg.design.blowing === undefined) {
    cfg.design.blowing = deriveBlowing(cfg.propeller);
  }
  cfg.schemaVersion = HIGH_LIFT_SCHEMA_VERSION;
  return cfg;
}

/** A blowing source's centre as a `[x, z]` pair (for the disk render / handle position). */
export function blowingCenter(source: BlowingSource): V2 {
  return [source.x, source.z];
}

// ---------------------------------------------------------------------------
// Factories — add new elements / blowing sources to the collection
// ---------------------------------------------------------------------------

/** Next suffix for ids of added (non-default) collection entries, seeded from persisted ids. */
function nextExtraId(prefix: 'vane' | 'blow' | 'flap' | 'flpn' | 'slat' | 'krug', existingIds: Iterable<string>): string {
  const stem = `${prefix}-x`;
  let seq = 0;
  for (const id of existingIds) {
    if (!id.startsWith(stem)) continue;
    const suffix = Number(id.slice(stem.length));
    if (Number.isInteger(suffix) && suffix > seq) seq = suffix;
  }
  return `${stem}${seq + 1}`;
}

/**
 * A new free-form vane element, seeded from an existing vane's shape and offset aft+down so the
 * duplicate is visibly distinct. Gets a fresh id (never a DEFAULT_* id) + its OWN cloned shape, so
 * it's an EXTRA element (not a projection of the legacy `design.vane`).
 */
export function makeVaneElement(
  seed: VaneShape,
  ordinal: number,
  existingIds: Iterable<string>,
): VaneBSplineElement {
  const dx = 0.12;
  const dz = -0.04;
  const shape: VaneShape = {
    te: [seed.te[0] + dx, seed.te[1] + dz],
    points: seed.points.map((p) => [p[0] + dx, p[1] + dz] as V2),
  };
  return {
    kind: 'vane-bspline',
    id: nextExtraId('vane', existingIds),
    label: `Vane ${ordinal + 1}`,
    shape,
  };
}

/** A new actuator-disk blowing source, seeded from an existing one and offset so it's distinct. */
export function makeBlowingSource(
  seed: BlowingSource,
  ordinal: number,
  existingIds: Iterable<string>,
): BlowingSource {
  return {
    id: nextExtraId('blow', existingIds),
    label: `Blowing ${ordinal + 1}`,
    x: seed.x,
    z: seed.z + seed.diameter * 0.8,
    diameter: seed.diameter,
    tiltDeg: seed.tiltDeg,
  };
}

// ---------------------------------------------------------------------------
// Plain flap / slat factories (FXC-9009 control-surface tranche, AC-6)
// ---------------------------------------------------------------------------

/** Linear interpolation of a tabulated (ascending-x) surface at chordwise `x` (clamped). */
function interpSurface(surface: V2[], x: number): number {
  if (surface.length === 0) return 0;
  const first = surface[0]!;
  const last = surface[surface.length - 1]!;
  if (x <= first[0]) return first[1];
  if (x >= last[0]) return last[1];
  for (let i = 1; i < surface.length; i++) {
    const a = surface[i - 1]!;
    const b = surface[i]!;
    if (x <= b[0]) {
      const t = (x - a[0]) / (b[0] - a[0]);
      return a[1] + (b[1] - a[1]) * t;
    }
  }
  return last[1];
}

/** Camber-midpoint y of the main airfoil at chordwise `x` (the default hinge height). */
export function camberMidpointY(coords: AirfoilCoords, x: number): number {
  return 0.5 * (interpSurface(coords.upper, x) + interpSurface(coords.lower, x));
}

/**
 * The SPLIT POINT of a surface for a hinge-concentric control surface (ported from the CS designer's
 * `compute_cs_shape.find_split_point`): the point P on `surface` near the hinge x where the radial
 * `P − H` is PERPENDICULAR to the surface tangent, i.e. the NEAREST point of that surface to the hinge
 * `H`. This is where the control surface separates from the wing (the cut lip) and its distance to
 * the hinge is the local spiral radius. Returns `{ x, z, radius }`, or `null` on a degenerate surface.
 */
export function findSplitPoint(
  surface: V2[],
  hinge: V2,
): { x: number; z: number; radius: number } | null {
  const [hx, hz] = hinge;
  // Window: ±0.15c about the hinge x (the surface is chord-normalized, chord = 1).
  const pts = surface.filter((p) => p[0] >= hx - 0.15 && p[0] <= hx + 0.15);
  const n = pts.length;
  if (n < 5) return null;
  const f = new Array<number>(n);
  for (let i = 0; i < n; i++) {
    const prev = pts[Math.max(0, i - 1)]!;
    const next = pts[Math.min(n - 1, i + 1)]!;
    let tx = next[0] - prev[0];
    let ty = next[1] - prev[1];
    const tn = Math.hypot(tx, ty);
    if (tn > 1e-12) { tx /= tn; ty /= tn; }
    const p = pts[i]!;
    f[i] = (p[0] - hx) * tx + (p[1] - hz) * ty; // dot(P − H, tangent)
  }
  for (let i = 0; i < n - 1; i++) {
    const fi = f[i]!;
    const fj = f[i + 1]!;
    if (fi * fj < 0) {
      const t = fi / (fi - fj);
      const a = pts[i]!;
      const b = pts[i + 1]!;
      const px = a[0] + t * (b[0] - a[0]);
      const pz = a[1] + t * (b[1] - a[1]);
      return { x: px, z: pz, radius: Math.hypot(px - hx, pz - hz) };
    }
  }
  return null;
}

/**
 * Build a {@link CoveCutouts} for a plain flap/slat from a hinge `x` and a `coveDepth`. The cove
 * back wall sits at `hingeX`; `coveDepth` opens the upper/lower lip cuts aft of the hinge (a
 * SLOTTED flap). `coveDepth <= 0` ⇒ all three coincide at `hingeX` (a SEALED flap — no slot).
 * Maps `coveDepth` onto the `upperCutX`/`lowerCutX` vs `coveX` offsets (DECISION-b).
 */
/**
 * Default FRISE horn arc size (fraction of chord) — the lower-nose beak's forward reach + radius
 * when {@link FlaperonDerivedElement.friseHornFraction} / {@link PlainFlapElement.friseHornFraction}
 * is unset. ~3%c gives a visible, aero-plausible horn ahead of the hinge.
 */
export const DEFAULT_FRISE_HORN_FRACTION = 0.03 as const;

export function coveFromHinge(hingeX: number, coveDepth: number): CoveCutouts {
  const depth = Number.isFinite(coveDepth) && coveDepth > 0 ? coveDepth : 0;
  return {
    coveX: hingeX,
    upperCutX: hingeX + depth,
    lowerCutX: hingeX + depth,
  };
}

/**
 * A new plain-flap (aileron) element. The flap is cut from the main at `hingeX` with a cove of
 * `coveDepth` (0 ⇒ sealed), and deflected `deflectionDeg` (TE-down positive; negative = aileron-up).
 * The aft nose mirrors {@link FlaperonShape}: slope-matched B-spline mating the cove. `hingeY`
 * defaults to the camber midpoint at `hingeX`.
 */
export function makePlainFlapElement(
  coords: AirfoilCoords,
  ordinal: number,
  existingIds: Iterable<string>,
  opts?: {
    hingeX?: number;
    coveDepth?: number;
    deflectionDeg?: number;
    hingeY?: number;
    gap?: number;
    /** HINGE-CONCENTRIC nose (internally-hinged sealed aileron). See {@link PlainFlapElement.concentricNose}. */
    concentricNose?: boolean;
  },
): PlainFlapElement {
  const hingeX = opts?.hingeX ?? 0.7;
  const coveDepth = opts?.coveDepth ?? 0.04;
  // `gap` (chords) opens a SLOT between the main cove wall and the flap nose: the main is still cut
  // at hingeX+coveDepth, but the flap nose starts `gap` further aft, so a real clearance opens
  // between the two elements. DEFAULT 0 keeps the historical sealed mating byte-identical for every
  // existing caller; the aircraft-CS seed passes the rig's true slot width here, so the plain flap
  // no longer buries its nose inside the main (which read as a zero-gap 5%-overlap "collision" and a
  // pinched aft profile).
  const gap = Number.isFinite(opts?.gap) && (opts?.gap ?? 0) > 0 ? (opts!.gap as number) : 0;
  const concentricNose = opts?.concentricNose === true;

  // ---- HINGE-CONCENTRIC aileron (opt-in — the internally-hinged sealed aileron) --------------------
  // Ported from the CS designer's proven `compute_cs_shape` spiral cove. The flap nose is a SPIRAL arc
  // about the hinge whose radius blends (cosine) between the lower + upper SPLIT radii — the
  // perpendicular (nearest-surface) distances from the hinge to each skin. The main cove is the SAME
  // spiral + `gap`. Both spirals share the angle→radius profile and rotate about the SAME hinge, so
  // the slot clearance stays exactly `gap` at EVERY deflection. The split points (where the radial
  // from the hinge is perpendicular to the surface) are where the flap separates from the wing, so the
  // nose fits the section by construction — no poke-through. Gated: absent flag ⇒ the legacy path below.
  if (concentricNose) {
    const hingeY = opts?.hingeY ?? camberMidpointY(coords, hingeX);
    const hinge: V2 = [hingeX, hingeY];
    const su = findSplitPoint(coords.upper, hinge);
    const sl = findSplitPoint(coords.lower, hinge);
    if (su && sl) {
      const aUp = Math.atan2(su.z - hingeY, su.x - hingeX);
      const aLo = Math.atan2(sl.z - hingeY, sl.x - hingeX);
      const cove: CoveCutouts = {
        // Lips = the split x's on each surface (where the flap separates from the wing).
        upperCutX: su.x,
        lowerCutX: sl.x,
        coveX: Math.max(0, hingeX - Math.max(su.radius, sl.radius)),
        arc: { hinge, rLo: sl.radius, rUp: su.radius, aLo, aUp, gap },
      };
      const shape: FlaperonShape = {
        // The flap's aft skins start at the split x's; the spiral nose is built by buildConcentricFlap.
        upperCutX: su.x,
        lowerCutX: sl.x,
        noseTangentUpper: 0.025,
        noseTangentLower: 0.05,
        noseTip: [hingeX - Math.max(su.radius, sl.radius), hingeY], // cosmetic; the builder uses the spiral
      };
      return {
        kind: 'plain-flap',
        id: nextExtraId('flap', existingIds),
        label: `Flap ${ordinal + 1}`,
        hingeX,
        hingeY,
        deflectionDeg: opts?.deflectionDeg ?? 20,
        cove,
        shape,
        concentricNose: true,
      };
    }
    // Degenerate section (no split point found) — fall through to the legacy plain flap below.
  }
  // ---- Legacy (non-concentric) plain flap — byte-identical to before -------------------------------
  const upperCutX = hingeX + (coveDepth > 0 ? coveDepth : 0) + gap;
  const lowerCutX = hingeX + (coveDepth > 0 ? coveDepth : 0) + gap;
  const cove = coveFromHinge(hingeX, coveDepth);
  // The aft nose mates the cove (offset aft by `gap` when a slot is asked for): cuts at the lip x's,
  // slope-matched tangent handles, a free nose tip set just forward of the hinge at the camber midline.
  const shape: FlaperonShape = {
    upperCutX,
    lowerCutX,
    noseTangentUpper: 0.025,
    noseTangentLower: 0.05,
    noseTip: [hingeX - 0.015 + gap, camberMidpointY(coords, hingeX + gap)],
  };
  const hingeY = opts?.hingeY ?? camberMidpointY(coords, hingeX);
  return {
    kind: 'plain-flap',
    id: nextExtraId('flap', existingIds),
    label: `Flap ${ordinal + 1}`,
    hingeX,
    hingeY,
    deflectionDeg: opts?.deflectionDeg ?? 20,
    cove,
    shape,
    ...(concentricNose ? { concentricNose: true } : {}),
  };
}

/**
 * @deprecated The CRUDE original slat factory (kind `plain-slat`) — the main's nose chunk rotated
 * about a hinge, no shaped slot. Retained only so legacy-persisted / Tailor configs that reference it
 * keep building; the palette + {@link addSlat} now emit the PROPER {@link makeSlatElement}. The main
 * nose is split off forward of `cutX`, and the slat droops `deflectionDeg` (LE-down/forward positive).
 */
export function makePlainSlatElement(
  coords: AirfoilCoords,
  ordinal: number,
  existingIds: Iterable<string>,
  opts?: { cutX?: number; hingeX?: number; deflectionDeg?: number; hingeY?: number },
): PlainSlatElement {
  const cutX = opts?.cutX ?? 0.08;
  const hingeX = opts?.hingeX ?? cutX;
  return {
    kind: 'plain-slat',
    id: nextExtraId('slat', existingIds),
    label: `Slat ${ordinal + 1}`,
    cutX,
    hingeX,
    hingeY: opts?.hingeY ?? camberMidpointY(coords, hingeX),
    deflectionDeg: opts?.deflectionDeg ?? 20,
  };
}

/**
 * A new PROPER leading-edge slat element (FXC-9009 LE-device tranche — replaces the crude
 * {@link makePlainSlatElement}). The slat forward of `cutX` is reshaped into a real slat profile
 * (faithful upper/LE skin + a concave cusped feathered underside), and the main carries a rounded
 * leading-edge cove so the two form a converging SLOT, rigged by `deflectionDeg` + `gap` + `overlap`.
 * `variant` selects slotted (default) / sealed (zero gap) / fixed-slot (permanent gap).
 */
export function makeSlatElement(
  coords: AirfoilCoords,
  ordinal: number,
  existingIds: Iterable<string>,
  opts?: {
    cutX?: number;
    hingeX?: number;
    deflectionDeg?: number;
    hingeY?: number;
    gap?: number;
    overlap?: number;
    slotDepth?: number;
    teThickness?: number;
    variant?: 'slotted' | 'sealed' | 'fixed-slot';
  },
): SlatElement {
  // Defaults are 30P30N-class transport-slat rigging: a 15%c slat at ~28° droop with a 2.5%c slot gap
  // and a deep cove hook (slotDepth), so a freshly-added slat reads like a real leading-edge slat.
  const cutX = opts?.cutX ?? 0.15;
  const hingeX = opts?.hingeX ?? cutX;
  return {
    kind: 'slat',
    id: nextExtraId('slat', existingIds),
    label: `Slat ${ordinal + 1}`,
    cutX,
    hingeX,
    hingeY: opts?.hingeY ?? camberMidpointY(coords, hingeX),
    deflectionDeg: opts?.deflectionDeg ?? 28,
    gap: opts?.gap ?? 0.025,
    overlap: opts?.overlap ?? 0.005,
    slotDepth: opts?.slotDepth ?? 0.05,
    teThickness: opts?.teThickness ?? 0.004,
    variant: opts?.variant ?? 'slotted',
  };
}

/**
 * A new KRUEGER flap element (FXC-9009 LE-device tranche). A panel hinged near the LOWER leading edge
 * (`lowerHingeX`, ~0.03c) that rotates DOWN+FORWARD; the panel runs from the hinge around the LE to
 * `noseCutX`. `bullnose` rounds the deployed LE. `hingeY` defaults to the lower-surface y at the hinge.
 */
export function makeKruegerElement(
  coords: AirfoilCoords,
  ordinal: number,
  existingIds: Iterable<string>,
  opts?: {
    lowerHingeX?: number;
    noseCutX?: number;
    deflectionDeg?: number;
    hingeY?: number;
    bullnose?: boolean;
    panelArcFraction?: number;
    panelCamber?: number;
    bullnoseRadius?: number;
    panelThickness?: number;
    gap?: number;
    overlap?: number;
    variant?: 'sealed' | 'slotted';
  },
): KruegerElement {
  // Defaults RAISED for the rebuilt Krueger (spec §4.3): hinge lowered (0.03→0.02) so a 60–80° swing
  // wraps the LE forward; nose cut aft (0.1→0.12); deflection up (45→65) into the real deployed band.
  const lowerHingeX = opts?.lowerHingeX ?? 0.02;
  const bullnose = opts?.bullnose ?? true;
  return {
    kind: 'krueger',
    id: nextExtraId('krug', existingIds),
    label: `Krueger ${ordinal + 1}`,
    lowerHingeX,
    hingeY: opts?.hingeY ?? interpSurface(coords.lower, lowerHingeX),
    noseCutX: opts?.noseCutX ?? 0.12,
    deflectionDeg: opts?.deflectionDeg ?? 65,
    bullnose,
    panelArcFraction: opts?.panelArcFraction ?? 0.15,
    panelCamber: opts?.panelCamber ?? 0.06,
    // Canonical nose radius. Legacy `bullnose:false` forces 0 (knife edge); otherwise an explicit
    // `bullnoseRadius` wins, else the default (D9 migration mapping, applied once at construction).
    bullnoseRadius: bullnose === false ? 0 : (opts?.bullnoseRadius ?? 0.018),
    panelThickness: opts?.panelThickness ?? 0.004,
    gap: opts?.gap ?? 0,
    overlap: opts?.overlap ?? 0,
    variant: opts?.variant ?? 'sealed',
  };
}

/**
 * Add a plain flap to a design IN PLACE (DECISION-c — implemented on the collection here, not a
 * later lib reorg). Materialises the explicit `design.elements` (so the legacy first-of-kind entries
 * keep their references), pushes the new flap element, and returns the added element + the updated
 * collection. The main-cove update is implicit: the flap carries its OWN cove and the geometry
 * builder re-cuts the main against it when building the flap (so a plain flap need NOT mutate the
 * default main-cove — the legacy main keeps its own cove, the flap its own).
 */
export function addPlainFlap(
  design: HighLiftDesign,
  coords: AirfoilCoords,
  opts?: { hingeX?: number; coveDepth?: number; deflectionDeg?: number; hingeY?: number; gap?: number; concentricNose?: boolean },
): PlainFlapElement {
  const els = (design.elements && design.elements.length > 0 ? design.elements : deriveElements(design)).slice();
  const ordinal = els.filter((e) => e.kind === 'plain-flap').length;
  const flap = makePlainFlapElement(coords, ordinal, els.map((e) => e.id), opts);
  els.push(flap);
  design.elements = els;
  return flap;
}

/**
 * Add a PROPER leading-edge slat to a design IN PLACE (FXC-9009 LE-device tranche — the fore mirror
 * of {@link addPlainFlap}, now emitting the shaped `slat` kind rather than the crude `plain-slat`).
 * The main carries a rounded LE cove and the slat a cusped underside, forming a converging slot.
 */
export function addSlat(
  design: HighLiftDesign,
  coords: AirfoilCoords,
  opts?: {
    cutX?: number;
    hingeX?: number;
    deflectionDeg?: number;
    hingeY?: number;
    gap?: number;
    overlap?: number;
    slotDepth?: number;
    teThickness?: number;
    variant?: 'slotted' | 'sealed' | 'fixed-slot';
  },
): SlatElement {
  const els = (design.elements && design.elements.length > 0 ? design.elements : deriveElements(design)).slice();
  const ordinal = els.filter((e) => e.kind === 'slat').length;
  const slat = makeSlatElement(coords, ordinal, els.map((e) => e.id), opts);
  els.push(slat);
  design.elements = els;
  return slat;
}

/** Add a Krueger flap to a design IN PLACE (FXC-9009 LE-device tranche). */
export function addKrueger(
  design: HighLiftDesign,
  coords: AirfoilCoords,
  opts?: {
    lowerHingeX?: number;
    noseCutX?: number;
    deflectionDeg?: number;
    hingeY?: number;
    bullnose?: boolean;
    panelArcFraction?: number;
    panelCamber?: number;
    bullnoseRadius?: number;
    panelThickness?: number;
    gap?: number;
    overlap?: number;
    variant?: 'sealed' | 'slotted';
  },
): KruegerElement {
  const els = (design.elements && design.elements.length > 0 ? design.elements : deriveElements(design)).slice();
  const ordinal = els.filter((e) => e.kind === 'krueger').length;
  const krueger = makeKruegerElement(coords, ordinal, els.map((e) => e.id), opts);
  els.push(krueger);
  design.elements = els;
  return krueger;
}

/**
 * Compose a full SLOTTED-FLAP SYSTEM (open cove + vane + slotted flaperon + deploy rig) onto a design
 * IN PLACE, copied from a reference `source` design (normally the DEFAULT high-lift airfoil). This is
 * the one-click "+ slotted flap (vane + flaperon)" path that unblocks composing Qiqi-style complex
 * devices onto a CS-seeded (clean / aileron) section, whose bare `main-cove` carries no cove for a
 * vane or flaperon to nest in.
 *
 * The default device stack is FOIL-AGNOSTIC: {@link buildConfiguration} re-derives the flaperon from
 * the CURRENT main coords and the vane is absolute-framed, so the copied system rides onto any base
 * foil (the FoilPicker replace-foil path proves the same). It therefore copies the parametric fields
 * — cove / vane / flaperon / deploy + flaperon hinges / deploy track / motion / vane follower — from
 * `source`, deep-cloned so the module-level default is never shared or mutated.
 *
 * `source` is PASSED IN (rather than importing `DEFAULT_HIGH_LIFT_AIRFOIL` here) to avoid an
 * elements.ts <-> estolConfig.ts runtime import cycle — estolConfig already value-imports this module.
 * `coords` is accepted for signature parity with the other add* factories + a future foil-fit hook;
 * the copied absolute/derived stack does not need it today.
 */
export function addSlottedFlapSystem(
  design: HighLiftDesign,
  coords: AirfoilCoords,
  source: HighLiftDesign,
  sourceCoords?: AirfoilCoords,
): { vane: VaneBSplineElement; flaperon: FlaperonDerivedElement } {
  // With sourceCoords the copied system is FITTED to this section (slot-frame
  // similarity map + vane clearance tuner - see fitElements.ts) instead of
  // riding the source's absolute coordinates onto a foil they were never
  // authored for (the raw copy parks the default vane half outside a thin
  // section's skin). Without it, the legacy verbatim copy is preserved.
  const fitted = sourceCoords
    ? fitSlottedSystemToSection(coords, source, sourceCoords, 0.003)
    : undefined;
  const src: HighLiftDesign = fitted ? { ...source, ...fitted } : source;
  const els = (design.elements && design.elements.length > 0 ? design.elements : deriveElements(design)).slice();
  // The design-level vane/flaperon fields are the FIRST-of-kind element's mutation surface:
  // `rebindLegacyElements` re-points the first vane/flaperon element's shape at them on every
  // load/migrate. So the copied system may only claim a projection that has NO existing owner —
  // overwriting design.vane while a first-of-kind vane element exists would silently swap that
  // element's shape for this system's copy on the next reload (and the same for the flaperon).
  const hasVane = els.some((e) => e.kind === 'vane-bspline');
  const hasFlaperon = els.some((e) => e.kind === 'flaperon-derived');

  // Copy the reference system's parametric fields (deep-cloned — never share the default).
  if (!hasVane) design.vane = structuredClone(src.vane);
  if (!hasFlaperon) design.flaperon = structuredClone(src.flaperon);
  if (!hasVane && !hasFlaperon) {
    // Bare main (the CS-seeded case this factory exists for): claim the cove + deploy rig too.
    // A design that already carries a device keeps its own cove and rig — they belong to the
    // existing system, and the cove is already open (a nested device requires it).
    design.cove = structuredClone(src.cove);
    design.deployHinge = structuredClone(src.deployHinge);
    design.flaperonHinge = structuredClone(src.flaperonHinge);
    if (src.deployTrack) design.deployTrack = structuredClone(src.deployTrack);
    if (src.motion) design.motion = structuredClone(src.motion);
    if (src.vaneFollower) design.vaneFollower = structuredClone(src.vaneFollower);

    // Open the (first) main-cove: swap its (possibly sealed-TE) cove for the reference OPEN cove so
    // the vane + flaperon nest in a real cove. The element's cove SHARES `design.cove` (so the legacy
    // cove sliders keep editing the same object — the shared ref survives structuredClone).
    const mainIdx = els.findIndex((e) => e.kind === 'main-cove');
    if (mainIdx >= 0) {
      const main = els[mainIdx]!;
      if (main.kind === 'main-cove') els[mainIdx] = { ...main, cove: design.cove };
    }
  }
  const ids = els.map((e) => e.id);
  // A first-of-kind vane/flaperon SHARES design.vane / design.flaperon (the legacy projection the
  // sliders edit; `rebindLegacyElements` keeps them pointed here across any later reload/migrate).
  // Additional ones get their OWN clone — the projection already has an owner.
  const vane: VaneBSplineElement = {
    kind: 'vane-bspline',
    id: nextExtraId('vane', ids),
    label: 'Vane',
    shape: hasVane ? structuredClone(src.vane) : design.vane,
  };
  ids.push(vane.id);
  const flaperon: FlaperonDerivedElement = {
    kind: 'flaperon-derived',
    id: nextExtraId('flpn', ids),
    label: 'Flaperon',
    shape: hasFlaperon ? structuredClone(src.flaperon) : design.flaperon,
  };
  els.push(vane, flaperon);
  design.elements = els;
  return { vane, flaperon };
}

/**
 * Compose a SINGLE-SLOTTED flap (open cove + slotted flap, NO vane) onto a design IN PLACE -
 * the simple slotted flap: the slot forms directly between the main's cove exit and the flap
 * nose. Same fitting, projection-claiming, and rig rules as {@link addSlottedFlapSystem},
 * minus the vane.
 */
export function addSingleSlottedFlap(
  design: HighLiftDesign,
  coords: AirfoilCoords,
  source: HighLiftDesign,
  sourceCoords?: AirfoilCoords,
): FlaperonDerivedElement {
  const fitted = sourceCoords
    ? fitSlottedSystemToSection(coords, source, sourceCoords, 0.003, { withVane: false })
    : undefined;
  const src: HighLiftDesign = fitted ? { ...source, ...fitted } : source;
  const els = (design.elements && design.elements.length > 0 ? design.elements : deriveElements(design)).slice();
  const hasVane = els.some((e) => e.kind === 'vane-bspline');
  const hasFlaperon = els.some((e) => e.kind === 'flaperon-derived');

  if (!hasFlaperon) design.flaperon = structuredClone(src.flaperon);
  if (!hasVane && !hasFlaperon) {
    design.cove = structuredClone(src.cove);
    design.deployHinge = structuredClone(src.deployHinge);
    design.flaperonHinge = structuredClone(src.flaperonHinge);
    if (src.deployTrack) design.deployTrack = structuredClone(src.deployTrack);
    if (src.motion) design.motion = structuredClone(src.motion);

    const mainIdx = els.findIndex((e) => e.kind === 'main-cove');
    if (mainIdx >= 0) {
      const main = els[mainIdx]!;
      if (main.kind === 'main-cove') els[mainIdx] = { ...main, cove: design.cove };
    }
  }
  const ids = els.map((e) => e.id);
  const flaperon: FlaperonDerivedElement = {
    kind: 'flaperon-derived',
    id: nextExtraId('flpn', ids),
    label: 'Flap',
    shape: hasFlaperon ? structuredClone(src.flaperon) : design.flaperon,
  };
  els.push(flaperon);
  design.elements = els;
  return flaperon;
}

/**
 * True when an id is the ONE structurally-mandatory entry — the main element (the base airfoil
 * every other builder/consumer assumes is present; `mainCoveOf` falls back to `cfg.design.cove`
 * rather than `null`, unlike `firstVaneOf`/`firstFlaperonOf`, which are already documented +
 * implemented as "or null if the section has no vane/flaperon" — a vane-less or flaperon-less (or
 * blowing-less) section is an intentionally-supported state, just previously unreachable via the
 * UI's remove button (FXC-9009: this used to also protect the default vane/flaperon/blowing-source,
 * which is why they couldn't be removed — relaxed so the default devices are removable like any
 * added one; only the main element stays permanently protected). */
export function isDefaultCollectionId(id: string): boolean {
  return id === DEFAULT_MAIN_ELEMENT_ID;
}
